import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { CurrenciesList } from '@/shared/lib/forms/asset-form/centralized-wallet';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import {
  DecentralizedWalletDetailsType,
  DecentralizedWalletGeneralType,
} from '../../sj-orm/models/assets/categories';
import { CryptoWalletType, CurrenciesType } from '../../sj-orm/models/assets/enums';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type DecentralizedWallet = DecentralizedWalletFormGeneralType &
  DecentralizedWalletFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

const CryptoWalletTypesList = [
  {
    label: 'forms_assets:decentralizedWallet.enums.walletType.hot',
    key: CryptoWalletType.hot,
  },
  {
    label: 'forms_assets:decentralizedWallet.enums.walletType.cold',
    key: CryptoWalletType.cold,
  },
];

type DecentralizedWalletHelpGeneralType = {
  nickname: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  walletType: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  assetsInWallet: QuestionType.SELECT_FROM_LIST;
  mnemonicStorageLocation: QuestionType.TEXT;
};

type DecentralizedWalletHelpDetailsType = {
  officialWalletName: QuestionType.TEXT;
  deviceStorageLocation: QuestionType.TEXT;
  loginStorageLocation: QuestionType.TEXT;
  attachments?: QuestionType.FILE_UPLOAD;
};

type DecentralizedWalletFormGeneralType = TDtoWrapper<
  DecentralizedWalletGeneralType,
  DecentralizedWalletHelpGeneralType
>;

type DecentralizedWalletFormDetailsType = TDtoWrapper<
  DecentralizedWalletDetailsType,
  DecentralizedWalletHelpDetailsType
>;

const DecentralizedWalletFormGeneral: DecentralizedWalletFormGeneralType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  walletType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:decentralizedWallet.inputs.walletType.label',
    value: CryptoWalletType.hot,
    required: true,
    items: CryptoWalletTypesList,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:decentralizedWallet.inputs.purchaseValue.label',
    tooltip: 'forms_assets:decentralizedWallet.inputs.purchaseValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  assetsInWallet: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:decentralizedWallet.inputs.assetsInWallet.label',
    value: [CurrenciesType.BTC],
    items: CurrenciesList,
    mode: 'multiple',
  },
  mnemonicStorageLocation: {
    type: QuestionType.TEXT,
    label: 'forms_assets:decentralizedWallet.inputs.mnemonicStorageLocation.label',
    tooltip: 'forms_assets:decentralizedWallet.inputs.mnemonicStorageLocation.tooltip',
    value: '',
    required: true,
    yupScheme: yup
      .string()
      .required('forms_assets:decentralizedWallet.inputs.mnemonicStorageLocation.errors'),
  },
};

const DecentralizedWalletFormDetails: DecentralizedWalletFormDetailsType = {
  officialWalletName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:decentralizedWallet.inputs.officialWalletName.label',
    tooltip: 'forms_assets:decentralizedWallet.inputs.officialWalletName.tooltip',
    order: 1,
  },
  deviceStorageLocation: {
    type: QuestionType.TEXT,
    label: 'forms_assets:decentralizedWallet.inputs.deviceStorageLocation.label',
    order: 2,
  },
  loginStorageLocation: {
    type: QuestionType.TEXT,
    label: 'forms_assets:decentralizedWallet.inputs.loginStorageLocation.label',
    order: 3,
  },
  kyc: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:decentralizedWallet.inputs.kyc.label',
    tooltip: 'forms_assets:decentralizedWallet.inputs.kyc.tooltip',
  },
  attachments: baseAssetFields.attachments,
};

const DecentralizedWalletForm = [
  DecentralizedWalletFormGeneral,
  DecentralizedWalletFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'taxAgent',
    'taxAgentOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { DecentralizedWalletForm };
